import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Authenticate } from 'src/app/models/server/authenticate';
import { Rights } from 'src/app/models/server/enums/rights';
import { ServiceCodes } from 'src/app/models/server/enums/service-codes';
import { Token } from 'src/app/models/server/token';
import { WebBrowserTabBlinkerService } from '../core/web-browser-tab-blinker-service';
import { ProfileSettingsStateService } from './profile-settings-state.service';
import { SessionTimeoutService } from './session-timeout.service';

@Injectable({
    providedIn: 'root',
})
export class ConnectedUsersStateService {
    private onUserConnectedChange = new BehaviorSubject<Authenticate>(null);

    constructor(
        private timeoutService: SessionTimeoutService,
        private profileSettingsStateService: ProfileSettingsStateService,
        private webBrowserTabBlinkerService: WebBrowserTabBlinkerService
    ) {
        let localSessionUser = Authenticate.from(JSON.parse(sessionStorage.getItem('user')));
        this.onUserConnectedChange = new BehaviorSubject<Authenticate>(localSessionUser);
    }

    get onUserConnectedChange$() {
        return this.onUserConnectedChange.asObservable();
    }

    // Retourne l'utilisateur connecté
    public getUserConnected(): Authenticate {
        return this.onUserConnectedChange.value;
    }

    // Specifie l'utilisateur connecté
    public setUserConnected(user: Authenticate) {
        console.log("Specifie l'utilisateur connecté", user);
        sessionStorage.setItem('user', JSON.stringify(user));

        if (user) {
            this.timeoutService.startTimer(user.token.expireOn);
            this.profileSettingsStateService.setProfileSettings(user.profileSettings);
        } else {
            this.timeoutService.stopTimer();
        }

        this.onUserConnectedChange.next(user);

        // Dans le cas où une notification d'expiration de session s'est affichée, arrête le clignotement de l'onglet si la session a expirée.
        this.webBrowserTabBlinkerService.stop();
    }

    public updateUserToken(token: any) {
        console.log("met à jour le token de l'utilisateur connecté");

        let connectedUser = this.getUserConnected();
        connectedUser.token = new Token();
        connectedUser.token.accessToken = token.accessToken;
        connectedUser.token.expireOn = new Date(token.expireOn);

        sessionStorage.setItem('user', JSON.stringify(connectedUser));
    }

    /**
     * Indique si l'utilisateur connecté possède le droit spécifié.
     * @param right : Le droit a tester.
     * @returns True, si l'utilisateur connecté possède le droit spécifié.
     */
    public hasRight(right: Rights): boolean {
        if (!!this.onUserConnectedChange.value.rights) {
            return this.onUserConnectedChange.value.rights.some((x) => x == right);
        } else {
            return false;
        }
    }

    /**
     * Indique si l'utilisateur connecté possède l'un des droits spécifiés.
     * @param expectedRights : Les droits a tester.
     * @returns True, si l'utilisateur connecté possède l'un des droits spécifiés.
     */
    public hasSomeRight(...expectedRights: Rights[]): boolean {
        if (!!this.onUserConnectedChange.value.rights) {
            return expectedRights.some((x) => this.onUserConnectedChange.value.rights.includes(x));
        } else {
            return false;
        }
    }

    /**
     * Indique si l'utilisateur connecté à accès au service spécifié.
     * @param service : Le service a tester.
     * @returns True, si l'utilisateur connecté a accès au service spécifié.
     */
    public hasServiceSubscription(service: ServiceCodes): boolean {
        if (!!this.onUserConnectedChange.value.serviceSubscriptions) {
            return this.onUserConnectedChange.value.serviceSubscriptions.some((x) => x.serviceCode === service && x.enabled === true);
        } else {
            return false;
        }
    }
}
